<template>
  <div class="details-component" v-if="permits.items">
    <div class="details-component__header">
      <h1 class="header">{{ $tc('common.permit', permits.total) }}</h1>
    </div>
    <a class="add-new-permit" @click="linkTo">
      <span class="name">{{ $t('add_permit.new_permit') }}</span>
    </a>
    <div class="sidebar-container">
      <div class="no-permits" v-if="(!permits.items || permits.items.length === 0)">
        <h1>{{ $t('end_user.no_permits') }}</h1>
      </div>
      <template v-for="permit in permits.items">
        <router-link :key="permit.id"
          :to="{ name: 'user-with-permit', params: { id: $route.params.id, permitId: permit.id } }"
          class="permit-block">

          <div :class="{ 'permit-header': isDuplicatedPermit(permit) }">
            <div class="permit-block__zone" v-if="permit.productName || permit.zone">
              <div class="permit-block__product" v-if="permit.productName">
                <span class="icon-product"></span>
                <span>{{ permit.productName }}</span>
              </div>
              <div v-if="permit.zone">
                <span class="fa fa-map-marker icon-zone"></span>
                <span>{{ permit.zone }}</span>
              </div>
            </div>
            <div class="icon-container" v-if="isDuplicatedPermit(permit)">
              <div class="permit-icon-container"><img src="~assets/duplicate-permit.svg" /></div>
            </div>
          </div>
          <div class="permit-block__identifiers">
            <div class="permit-block__identifiers__identity permit-plate" v-if="permit.vehicleReg">
              <div class="icon icon--car"></div>
              <p :class="{ 'smaller-font': permit.vehicleReg && permit.vehicleReg.length > 6 }">{{ permit.vehicleReg }}
              </p>
              <p class="permit-plate__spot" v-if="permit.parkingSpotNumber"> {{ permit.parkingSpotNumber }}</p>
            </div>
            <div class="permit-block__identifiers__identity permit-card permit-card__small"
              v-if="permit.parkingCardNr && permit.canUseCards">
              <div class="permit-card__number">{{ permit.parkingCardNr }}</div>
            </div>
            <div class="permit-block__identifiers__identity no-identity"
              v-if="!permit.vehicleReg && !permit.parkingCardNr">
              <span>
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                {{ $t('end_user.permit_needs_identity') }}
              </span>
            </div>
          </div>
          <div class="permit-card-validity" v-if="!isPermitExpired(permit) && permitValidityPeriod(permit).length > 0">
            {{ permitValidityPeriod(permit) }}</div>
          <div class="permit-card-expired" v-if="isPermitExpired(permit)">{{ $t('end_user.permit_expired',
            { expirationDate: permit.expirationDate }) }}</div>
        </router-link>
      </template>
    </div>
  </div>
</template>

<script type="text/javascript">
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import moment from 'moment';
import ListModal from 'common/ListModal';
import WarningModal from 'common/WarningModal';
import Tooltip from 'common/Tooltip';

export default {
  data() {
    return {
      activePermit: null,
      isMobileScreen: false,
    };
  },
  components: {
    ListModal,
    WarningModal,
    Tooltip,
  },
  computed: mapGetters({
    permits: 'landlord/permits/permits',
    user: 'user/user/user',
  }),
  created() {
    window.addEventListener('resize', this.setIsMobileScreen);
    this.setIsMobileScreen();
  },
  methods: {
    navigateToEditViewPermit(event, permitId) {
      event.preventDefault();
      this.$router.push({ name: 'edit-user-permit', params: { id: permitId } });
    },
    isDuplicatedPermit(permitToSearch) {
      const filteredPermits = this.permits.items.filter(permit => permit.vehicleReg === permitToSearch.vehicleReg && permit.productName === permitToSearch.productName)
      return filteredPermits.length > 1;
    },
    setIsMobileScreen() {
      if (window.innerWidth > 760) {
        this.isMobileScreen = false;
      } else {
        this.isMobileScreen = true;
      }
    },
    permitValidityPeriod(permit) {
      if (permit.validFromDate && !permit.expirationDate) {
        return this.$t('end_user.permit_validity_from', { from: permit.validFromDate });
      } else if (!permit.validFromDate && permit.expirationDate) {
        return this.$t('end_user.permit_validity_to', { to: permit.expirationDate });
      } else if (permit.validFromDate && permit.expirationDate) {
        return this.$t('end_user.permit_validity', { from: permit.validFromDate, to: permit.expirationDate });
      }

      return '';
    },
    isPermitExpired(permit) {
      if (!permit.expirationDate) {
        return false;
      }

      return moment().isAfter(moment(permit.expirationDate, 'DD.MM.YYYY'), 'day');
    },
    linkTo() {
      this.$router.push({ name: 'add-user-with-permit', params: { id: this.$route.params.id } });
    },
  },
};

</script>

<style media="screen" scoped lang="scss">
@import '~styles/app.scss';
@import '~styles/_animations.scss';
@import '~styles/blocks/permit.scss';

.permit-header {
  display: flex;
  gap: 8px;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #00D1B2;
  border-radius: 5px;
  flex-basis: 15%;
}

.permit-icon-container {
  display: flex;
}

.add-new-permit {
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: var(--brand-orange);
  border: 1px solid var(--brand-orange);
  min-height: 72px;
  line-height: 52px;
  margin-bottom: 10px;
  cursor: pointer;
  @include shadow(1);
  padding: 10px;
  margin: 5px;

  @media screen and (max-width: 760px) {
    width: 100%;
  }

  &:hover {
    transition: all 325ms ease-in-out;
    opacity: 1;
  }

  &.active {
    opacity: 1;
    background-color: var(--white);
    @include shadow(2);
  }

  .name {
    font-size: 20px;
    border-radius: 6px;
  }
}

.no-permits {
  background: var(--brand-red);
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  color: var(--white);
  padding: 20px;
  flex-direction: column;

  @media screen and (max-width: 1115px) {
    margin: 0 0 45px 0;
  }
}

.sidebar-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.icon-zone {
  margin: 0 5px 0 4px;
}

.tooltip {
  top: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/users/PermitsSidebar.vue